/**
 * The docs navigation tree, derived from page metadata.
 *
 * Every page declares `export const meta = { title, category, description }`
 * and `npm run docs:index` writes that into a per-audience manifest. This
 * module loads the manifests a reader is entitled to and groups them into
 * categories within spaces. There is **no navigation literal anywhere** —
 * the sidebar, the Categories menu, the landing page, the category
 * indexes and the breadcrumbs all read this, so they cannot disagree with
 * each other or with the pages.
 *
 * That is the point of the module. `DocsSidebar.jsx` used to carry a
 * 400-line literal restating a `meta.section` every page already
 * declared, and the two had drifted: pages existed on disk that appeared
 * in no section, and one section held thirty of the site-admin pages in a
 * single undifferentiated list.
 *
 * ## Exposure
 *
 * The manifests travel the same channel as the search shards and the MDX
 * pages: `import.meta.glob`, with only the audiences a reader passes
 * actually loaded. That is **nav parity, not a confidentiality
 * boundary** — exactly as `docAccess.js` sets out at length. Every shard
 * and every page chunk is in the client bundle and can be fetched by
 * asking for its URL, and adding a manifest changes nothing about that in
 * either direction. Do not describe this filtering as gating; anything
 * that needs secrecy belongs behind a backend check.
 *
 * ## Spaces
 *
 * Derived from the resolved audience via `spaceForAudience`, never
 * declared in `meta`. See `docsCategory.js` for why the audience and not
 * the directory carries this.
 */
import { canViewAudience } from './docAccess.js'
import { DOC_AUDIENCES } from './docAccessRules.js'
import { groupNavPages } from './docsNavTree.js'

const manifestLoaders = import.meta.glob('./generated/docs-nav.*.json', {
    import: 'default'
})

function loaderFor(audience) {
    const suffix = `docs-nav.${audience}.json`
    const key = Object.keys(manifestLoaders).find((k) => k.endsWith(suffix))
    return key ? manifestLoaders[key] : undefined
}

/**
 * In-flight and settled manifests, keyed by audience. Promises rather
 * than results, so two components mounting at once share one fetch.
 */
const manifestCache = new Map()

function loadManifest(audience) {
    const cached = manifestCache.get(audience)
    if (cached) return cached

    const loader = loaderFor(audience)
    const promise = loader
        ? loader().then((entries) =>
              entries.map((entry) => ({ ...entry, audience }))
          )
        : // A missing manifest means the index was never built, or an
          // audience was added without a rebuild. Degrade to "this
          // audience contributes nothing" rather than emptying the nav
          // for the audiences that did load.
          Promise.resolve(null)

    manifestCache.set(audience, promise)
    return promise
}

/**
 * The categories this reader may browse.
 *
 * A category exists exactly when at least one page declares it *and* that
 * page is visible to this reader. There is no registry of category names,
 * so an empty category cannot render, and deleting the last page in one
 * removes it everywhere with no second edit.
 *
 * Returns `{ categories, unavailable }`. `unavailable` lists audiences
 * the reader is entitled to whose manifest could not be loaded, so a
 * surface can say "this list may be incomplete" instead of quietly
 * showing less than it should. It is never populated for an audience the
 * reader has no claim on: nothing they may read is missing, so there is
 * nothing to tell them.
 */
export async function loadDocsNav(user) {
    const permitted = DOC_AUDIENCES.filter((audience) =>
        canViewAudience(audience, user)
    )

    const loaded = await Promise.all(
        permitted.map((audience) =>
            loadManifest(audience).then(
                (entries) => ({ audience, entries }),
                () => ({ audience, entries: null })
            )
        )
    )

    const pages = []
    const unavailable = []
    for (const { audience, entries } of loaded) {
        if (entries) pages.push(...entries)
        else unavailable.push(audience)
    }

    return { categories: groupNavPages(pages), unavailable }
}

/*
 * Selectors live in `docsNavTree.js` beside the grouping they describe.
 * Re-exported here so consumers have one import for the whole nav API and
 * do not have to know which half is glob-free.
 */
export {
    categoriesInSpace,
    categoryBySlug,
    categoryForPath,
    spacesWithContent
} from './docsNavTree.js'

/** Test seam: drop cached manifests so a fresh permission set reloads. */
export function __resetDocsNavCache() {
    manifestCache.clear()
}
